// Přepínač CZ ↔ SK (rozhodnutí Tomáše 2026-10-05; mock a měření
// ops/sweeps/2026-10-05-prepinac-cz-sk/). Nahrazuje „jen odkaz v patičce"
// z 27. 8. Druhý shop zůstává samostatný e-shop, ne jazyková mutace, proto
// přepínač ukazuje zemi a shop (vlajka, kód země, doména), ne jazyk.
//
// Jeden cíl (sisterTarget.js), tři místa:
//   - topbar: dvě vlajky na začátku řady před Blogem (initCountrySwitch;
//     do 7. 10. 2026 stály za pillem Sledování objednávky),
//   - mobilní menu: poslední řádek sekce shopu (mobileNav.js → sisterLink),
//   - patička: odkaz ve sloupci firmy (siteFooter.js → sisterLink).
// Na detailu produktu se cíl dohledá v API hledání (searchApi.sisterPath) až na
// záměr kliknout (najetí, focus, dotyk), takže stránku nezpomalí. Bez odpovědi
// (API bez routy, výpadek, offline fixture, produkt jen na jednom shopu) vede
// odkaz na homepage druhého shopu. Pokladna přepínač nemá: topbar, menu
// i patičku tam schovává html.bs-co-ready.
//
// Pod 1460 px se topbar CZ s ikonami nevejde: CSS položkám bere ikony a vlajkám
// kód země (_country-switch.scss, měření v sweepu).

import SHOP from '../../config/shop.js';
import SISTER from '../../config/sister.js';
import { icon } from './icon.js';
import { shoptetData } from '../shoptet-hooks.js';
import { sisterTarget } from './sisterTarget.js';
import { sisterPath } from './searchApi.js';
import L from '../locale/cs/countrySwitch.js';

// Klik před odpovědí API čeká nejdéle tak dlouho, pak jde na homepage.
const LOOKUP_WAIT_MS = 1500;

let target = null;
let lookup = null;
let settled = false;
let resolvedHref = null;
let bound = false;

/** Vlajka země `country` (CZ, SK) ze spritu: 3:2, zaoblená, s jemnou linkou. */
export function flag(country) {
  const box = document.createElement('span');
  box.className = 'bs-flag';
  box.appendChild(icon('flag-' + country.toLowerCase(), 'bs-flag__img'));
  return box;
}

function countryLabel(country, domain) {
  return (L.names[country] || country) + ', ' + domain;
}

// Cíl pro tuhle stránku; počítá se jednou (adresa ani produkt se nemění).
function currentTarget() {
  if (!target) {
    const product = shoptetData('product');
    const codes = [];
    const add = (code) => {
      if (code && codes.indexOf(String(code)) < 0) codes.push(String(code));
    };
    if (product) {
      (Array.isArray(product.codes) ? product.codes : []).forEach((c) => add(c && c.code));
      add(product.code);
    }
    target = sisterTarget(window.location, { pageType: shoptetData('pageType'), codes: codes }, SHOP, SISTER);
  }
  return target;
}

/**
 * Udělá z `a` odkaz na druhý shop: cíl podle stránky, jazyk cíle a značka
 * `data-bs-sister`, přes kterou se na detailu dosadí adresa téhož produktu.
 * Otevírá se ve stejném okně, přepnutí shopu je navigace, ne odkaz ven.
 */
export function sisterLink(a) {
  a.href = resolvedHref || currentTarget().href;
  a.setAttribute('hreflang', SISTER.language);
  a.setAttribute('data-bs-sister', '');
  return a;
}

// Cestu z API bere jen na originu druhého shopu: `//host` nebo `/\host`
// by prohlížeč četl jako jiný host.
function sisterUrl(path) {
  try {
    const u = new URL(path, SISTER.origin);
    return u.origin === SISTER.origin ? u.href : null;
  } catch (e) {
    return null;
  }
}

function resolveProduct() {
  if (!lookup) {
    lookup = sisterPath(currentTarget().codes).then(
      (path) => {
        const href = path ? sisterUrl(path) : null;
        if (href) {
          resolvedHref = href;
          document.querySelectorAll('a[data-bs-sister]').forEach((a) => {
            a.href = href;
          });
        }
        settled = true;
        return href;
      },
      () => {
        settled = true;
        return null;
      },
    );
  }
  return lookup;
}

function sisterAnchor(e) {
  const t = e.target;
  return t && t.closest ? t.closest('a[data-bs-sister]') : null;
}

function onIntent(e) {
  if (sisterAnchor(e)) resolveProduct();
}

// Klik dřív, než API odpověděl: počkat na adresu produktu, nejdéle LOOKUP_WAIT_MS.
// Klik do nové karty (prostřední tlačítko, Ctrl…) jde po tom, co v odkazu už je.
function onClick(e) {
  const a = sisterAnchor(e);
  if (!a || settled || e.defaultPrevented) return;
  if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  e.preventDefault();
  let gone = false;
  const go = (href) => {
    if (gone) return;
    gone = true;
    window.location.assign(href || a.href);
  };
  resolveProduct().then(go);
  setTimeout(() => go(null), LOOKUP_WAIT_MS);
}

function bindProductLookup() {
  if (bound || !currentTarget().codes.length) return;
  bound = true;
  document.addEventListener('pointerover', onIntent, { passive: true });
  document.addEventListener('focusin', onIntent);
  document.addEventListener('touchstart', onIntent, { passive: true });
  document.addEventListener('click', onClick);
}

// Položka topbaru: aktuální shop je jen značka „jste tady" (čtečka ji přeskočí,
// nav nese jediný odkaz), druhý shop odkaz s aria-label země a domény.
function topbarItem(country, domain, current) {
  const node = document.createElement(current ? 'span' : 'a');
  node.className = 'bs-country__item' + (current ? ' bs-country__item--current' : '');
  node.title = countryLabel(country, domain);
  if (current) {
    node.setAttribute('aria-hidden', 'true');
  } else {
    sisterLink(node);
    node.setAttribute('aria-label', node.title);
  }
  node.appendChild(flag(country));
  const code = document.createElement('span');
  code.className = 'bs-country__code';
  code.textContent = country;
  node.appendChild(code);
  return node;
}

export function initCountrySwitch() {
  const bar = document.querySelector('.top-navigation-bar .container');
  if (bar && !bar.querySelector('.bs-country')) {
    const nav = document.createElement('nav');
    nav.className = 'bs-country';
    nav.setAttribute('aria-label', L.nav);
    const items = [topbarItem(SHOP.shop.country, SHOP.shop.domain, true), topbarItem(SISTER.country, SISTER.domain, false)];
    // pořadí podle kódu země, ať vlajky stojí na obou shopech stejně (CZ vlevo)
    if (SISTER.country < SHOP.shop.country) items.reverse();
    items.forEach((node) => nav.appendChild(node));
    // na začátek řady, hned před odkazy (Tomáš 2026-10-07)
    const links = bar.querySelector('.bs-topbar-links');
    if (links) links.before(nav);
    else bar.insertBefore(nav, bar.firstChild);
  }
  bindProductLookup();
}
